import * as React from "react";

import { Alert, Button, Card, CardBody, CardHeader, EmptyState, StatCard } from "Components/ui";
import {
  CalendarClock,
  CirclePause,
  CirclePlay,
  Copy,
  IndianRupee,
  Mail,
  ReceiptText,
  Repeat,
  TriangleAlert,
} from "Components/ui/icons";
import { SchedulePeriod, TenantBilling } from "api/billing";
import { reminderChannelsLabel, reminderRecipients } from "../rules";
import { TenantLinkStatus } from "api/teleopdLink";
import { cn } from "utils/cn";
import { formatDate, formatDateTime, formatINR } from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import { useNow } from "Routes/Billing/hooks";
import { useAccountActions, useInvoiceActions } from "../data";
import { BillingPermissions, TenantBillingTab } from "../types";
import ActivityFeed from "./ActivityFeed";
import FocusTimeline from "./FocusTimeline";
import { copyPayLink, ExtendDueDateDialog } from "./InvoiceDetailModal";
import PauseEnforcementDialog from "./PauseEnforcementDialog";
import TeleopdPreview from "./TeleopdPreview";

type OverviewDialog = "extend" | "pause" | "resume" | "remind" | null;

interface OverviewTabProps {
  tenantId: string;
  billing: TenantBilling;
  link: TenantLinkStatus | undefined;
  permissions: BillingPermissions;
  onOpenInvoice: (invoiceId: string) => void;
  onRecordPayment: (invoiceId: string | null) => void;
  onGoToTab: (tab: TenantBillingTab) => void;
}

const QuickAction: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  disabledHint?: string;
}> = ({ icon, label, hint, onClick, disabled = false, disabledHint }) => (
  <Button
    variant="secondary"
    onClick={onClick}
    disabled={disabled}
    title={disabled ? disabledHint : undefined}
    fullWidth
    className="h-auto items-start justify-start gap-3 whitespace-normal px-3 py-2.5 text-left"
  >
    <span
      aria-hidden="true"
      className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-primary-subtle text-primary-text"
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-foreground">{label}</span>
      <span className="block text-xs font-normal text-muted-foreground">
        {disabled && disabledHint ? disabledHint : hint}
      </span>
    </span>
  </Button>
);

const OverviewTab: React.FC<OverviewTabProps> = ({
  tenantId,
  billing,
  link,
  permissions,
  onOpenInvoice,
  onRecordPayment,
  onGoToTab,
}) => {
  const now = useNow(30000);
  const [dialog, setDialog] = React.useState<OverviewDialog>(null);
  const invoiceActions = useInvoiceActions(tenantId);
  const accountActions = useAccountActions(tenantId);
  const { state, account, schedules, invoices, activity } = billing;
  const focus = state.focusInvoice;
  const payUrl = (state.notice && state.notice.payUrl) || (focus && focus.payUrl) || null;
  const hasOpenInvoice =
    Boolean(focus) ||
    invoices.some((invoice) => invoice.status === "issued" || invoice.status === "partially_paid");
  const recipients = reminderRecipients(account.contacts, billing.notifications);
  const channelsLabel = reminderChannelsLabel(billing.notifications);

  const nextCharge = schedules
    .filter((schedule) => schedule.status === "active" && schedule.nextPeriod)
    .map((schedule) => ({ name: schedule.name, period: schedule.nextPeriod as SchedulePeriod }))
    .sort((a, b) => (a.period.dueDate < b.period.dueDate ? -1 : 1))[0];

  const quickActions: React.ReactNode[] = [];
  if (permissions.canApprove) {
    quickActions.push(
      <QuickAction
        key="pay"
        icon={<IndianRupee aria-hidden="true" />}
        label="Record payment"
        hint="Bank transfer, UPI, cheque or cash received."
        onClick={() => onRecordPayment(focus ? focus._id : null)}
        disabled={!hasOpenInvoice}
        disabledHint="No open invoice to pay."
      />,
      <QuickAction
        key="extend"
        icon={<CalendarClock aria-hidden="true" />}
        label="Extend due date"
        hint={
          focus ? `Move ${focus.number || "the invoice"} past ${formatDate(focus.dueDate)}.` : ""
        }
        onClick={() => setDialog("extend")}
        disabled={!focus}
        disabledHint="Nothing is due."
      />,
      state.paused ? (
        <QuickAction
          key="resume"
          icon={<CirclePlay aria-hidden="true" />}
          label="Resume enforcement"
          hint={
            account.override
              ? `Paused through ${formatDate(account.override.pausedUntil)}; end it now.`
              : "End the pause now."
          }
          onClick={() => setDialog("resume")}
        />
      ) : (
        <QuickAction
          key="pause"
          icon={<CirclePause aria-hidden="true" />}
          label="Pause enforcement"
          hint="No warnings or read-only until a date."
          onClick={() => setDialog("pause")}
        />
      )
    );
  }
  if (permissions.canWrite) {
    quickActions.push(
      <QuickAction
        key="remind"
        icon={<Mail aria-hidden="true" />}
        label="Send reminder"
        hint={
          !channelsLabel
            ? "Neither email nor WhatsApp reminders are set up."
            : recipients.length > 0
            ? `By ${channelsLabel} to ${
                recipients.length === 1 ? recipients[0] : `${recipients.length} contacts`
              }.`
            : "Add billing contacts on the Rules tab."
        }
        onClick={() => setDialog("remind")}
        disabled={!focus || recipients.length === 0}
        disabledHint={
          !focus
            ? "Nothing is due."
            : !channelsLabel
            ? "No reminder channel is set up."
            : "No billing contacts yet."
        }
      />
    );
  }
  quickActions.push(
    <QuickAction
      key="link"
      icon={<Copy aria-hidden="true" />}
      label="Copy pay link"
      hint="Stable link the client can open any time."
      onClick={() => copyPayLink(payUrl)}
      disabled={!payUrl}
      disabledHint={focus ? "Pay links need SAP_PUBLIC_BASE_URL on the server." : "Nothing is due."}
    />
  );

  const pastDue = state.pastDueAmount > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Due now"
          value={formatINR(state.amountDue)}
          hint="Shown on TeleOpd's notice"
          icon={<IndianRupee aria-hidden="true" />}
          tone={pastDue ? "danger" : "neutral"}
        />
        <StatCard
          label="Outstanding"
          value={formatINR(state.outstanding)}
          hint="All open invoices"
          icon={<ReceiptText aria-hidden="true" />}
        />
        <StatCard
          label="Past due"
          value={formatINR(state.pastDueAmount)}
          icon={<TriangleAlert aria-hidden="true" />}
          tone={pastDue ? "danger" : "neutral"}
        />
        <StatCard
          label="Next charge"
          value={nextCharge ? formatINR(nextCharge.period.total) : "—"}
          hint={
            nextCharge
              ? `${formatDate(nextCharge.period.dueDate)} · ${nextCharge.name}`
              : "No active recurring charge"
          }
          icon={<Repeat aria-hidden="true" />}
        />
      </div>

      {state.paused && (
        <Alert
          tone="info"
          title={`Enforcement paused until ${formatDateTime(state.pausedUntil)}`}
          action={
            permissions.canApprove ? (
              <Button variant="secondary" size="sm" onClick={() => setDialog("resume")}>
                Resume now
              </Button>
            ) : undefined
          }
        >
          {account.override?.reason || "No reason recorded."}
          {account.override?.at ? ` · set ${formatDateTime(account.override.at)}` : ""}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Current invoice"
              description="The earliest unpaid invoice drives warnings and read-only."
              actions={
                focus ? (
                  <Button
                    variant="primary-ghost"
                    size="sm"
                    onClick={() => onOpenInvoice(focus._id)}
                  >
                    Open invoice
                  </Button>
                ) : undefined
              }
            />
            {focus ? (
              <CardBody className="space-y-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-foreground">
                      <span className={cn(focus.number && "font-mono")}>
                        {focus.number || "Unnumbered"}
                      </span>
                      {focus.title ? (
                        <span className="font-normal text-muted-foreground"> · {focus.title}</span>
                      ) : null}
                    </p>
                    <p className="text-sm text-muted-foreground">Due {formatDate(focus.dueDate)}</p>
                  </div>
                  <p className="text-2xl font-semibold tabular-nums text-foreground">
                    {formatINR(focus.balance)}
                    <span className="ml-1 text-sm font-normal text-muted-foreground">open</span>
                  </p>
                </div>
                <FocusTimeline invoice={focus} state={state} now={now} />
              </CardBody>
            ) : (
              <EmptyState
                compact
                icon={<ReceiptText aria-hidden="true" />}
                title="Nothing is due"
                description={
                  nextCharge
                    ? `Next invoice: ${formatINR(nextCharge.period.total)} due ${formatDate(
                        nextCharge.period.dueDate
                      )}, issued ${formatDate(nextCharge.period.issueDate)}.`
                    : "Add a recurring charge or a one-time invoice on the Charges tab."
                }
                action={
                  !nextCharge && permissions.canWrite ? (
                    <Button variant="secondary" size="sm" onClick={() => onGoToTab("charges")}>
                      Go to Charges
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Card>

          <Card>
            <CardHeader
              title="What TeleOpd users see now"
              description="A preview of the in-app notice for this client's staff."
            />
            <CardBody>
              <TeleopdPreview
                state={state}
                audienceNames={account.policy.audienceRoleNames.filter(Boolean)}
                link={link}
              />
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Quick actions" />
            <CardBody className="space-y-2">{quickActions}</CardBody>
          </Card>
          <Card>
            <CardHeader title="Activity" />
            <CardBody>
              <ActivityFeed entries={activity} onOpenInvoice={onOpenInvoice} limit={10} />
            </CardBody>
          </Card>
        </div>
      </div>

      {dialog === "extend" && focus && (
        <ExtendDueDateDialog
          invoice={focus}
          onConfirm={(dueDate, reason) =>
            invoiceActions.extend.mutateAsync({ invoiceId: focus._id, dueDate, reason })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "pause" && (
        <PauseEnforcementDialog
          onConfirm={(pausedUntil, reason) =>
            accountActions.pause.mutateAsync({ pausedUntil, reason })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "resume" && (
        <ConfirmDialog
          title="Resume enforcement now?"
          description="Warnings and read-only apply again from TeleOpd's next sync (SAP nudges it right away)."
          confirmLabel="Resume enforcement"
          requireReason
          onConfirm={(reason) => accountActions.resume.mutateAsync(reason)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "remind" && focus && (
        <ConfirmDialog
          title="Send a reminder?"
          description={`Sends ${focus.number || "the invoice"} (${formatINR(
            focus.balance
          )} open) with the pay link by ${channelsLabel || "email"} to ${recipients.join(", ")}.`}
          confirmLabel="Send reminder"
          onConfirm={() => invoiceActions.remind.mutateAsync(focus._id)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default OverviewTab;
